import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from "react-router-dom";
import PropagateLoader from "react-spinners/PropagateLoader";
import { toast } from 'react-toastify';
import { isNumber } from '../../Utils/Common';
import { postRequest } from '../../Utils/AppApi';
import {
    AIRTEL_IMW_CHECK_SENDER,
    AIRTEL_IMW_GET_SENDER_LIMIT,
    POST_DMT_INSTANT_PAY_CUSTOMER_INFO,   // ✅ New import
} from '../../Utils/appConstants';
import AddCustomer from '../moneyTransferV2/addCustomer';
import ViewCustomer from './viewCustomer';

// Theme colours
const primaryRed = '#C9262C';
const primaryOrange = '#FF8C00';
const primaryGreen = '#02A650';
const cardBorder = '#E5E7EB';

// ✅ Same layout constants as viewCustomer.jsx
const DMT_PAGE_WRAPPER = {
    background: 'linear-gradient(160deg, #FFFBEB 0%, #FFF8F5 25%, #FFFBEB 50%, #F8F9FF 75%, #FFFBEB 100%)',
    padding: '20px 0',
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
};

const DMT_PAGE_CONTAINER = {
    // maxWidth: '1800px',
    margin: '0 auto',
    padding: '0 15px',
    position: 'relative',
    zIndex: 2,
    width: '100%',
};

const AirtelImwDMT = (props) => {
    const history = useHistory();
    const [isLoading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [isCustomerInfo, setCustomerInfo] = useState(null);
    const [isCustomerNotExist, setCustomerNotExist] = useState(false);
    const [ineligibleReason, setIneligibleReason] = useState(null);
    const [isProcessing, setProcessing] = useState(false);
    const [regToken, setRegToken] = useState('');

    const mobileRef = useRef(searchText);

    useEffect(() => {
        mobileRef.current = searchText;
    }, [searchText]);

    useEffect(() => {
        if (props.mobile) {
            setSearchText(props.mobile);
            setTimeout(() => {
                getCustomerInfo();
            }, 300);
        }
    }, [props.mobile]);

    useEffect(() => {
        if (searchText && searchText.length === 10) {
            const timer = setTimeout(() => {
                getCustomerInfo();
            }, 800);
            return () => clearTimeout(timer);
        }
    }, [searchText]);

    /**
     * ✅ Calls the InstantPay customer-info API to obtain the referenceKey.
     * Used when the Airtel check-sender says the customer is not registered.
     */
    const getInstantPayReferenceKey = async (mobile) => {
        try {
            const payload = { customerMobile: mobile };
            const response = await postRequest(POST_DMT_INSTANT_PAY_CUSTOMER_INFO, payload, history);

            console.log('[AirtelImwDMT] InstantPay customer-info response =', response);

            // Success — referenceKey lives at response.data.referenceKey
            if (response?.data?.referenceKey) {
                return {
                    refKey: response.data.referenceKey,
                    data: response.data,
                    needOtp: response.isotp === true,
                };
            }

            // Failure — but backend may still return a referenceKey in data
            if (response?.data && response?.data?.referenceKey) {
                return {
                    refKey: response.data.referenceKey,
                    data: response.data,
                    needOtp: false,
                };
            }

            // No referenceKey anywhere
            if (response?.message) {
                toast.error(response.message);
            }
            return null;
        } catch (err) {
            console.error('[AirtelImwDMT] InstantPay referenceKey error:', err);
            toast.error('Failed to fetch InstantPay reference');
            return null;
        }
    };

    const getCustomerInfo = async () => {
        if (isProcessing) return;
        const mobile = mobileRef.current;

        if (!mobile) {
            toast.error("Customer Mobile Number is required");
            return;
        }
        if (mobile.length !== 10) {
            toast.error("Please enter a valid 10-digit Mobile Number");
            return;
        }

        setLoading(true);
        setIneligibleReason(null);
        const payload = { senderMobile: mobile };

        try {
            const response = await postRequest(AIRTEL_IMW_CHECK_SENDER, payload, history);

            console.log('[AirtelImwDMT] check-sender response =', response);

            // ---- Case 1: Already registered with Airtel ----
            if (response.success === true && response.isRegistered === true) {
                await getSenderLimit(mobile);
                setRegToken('');
                setCustomerNotExist(false);
            }

            // ---- Case 2: Need registration → call InstantPay first ----
            else if (
                response.needRegistration === true ||
                response.isRegistered === false
            ) {
                console.log('[AirtelImwDMT] Not registered → calling InstantPay');

                const instantPayResult = await getInstantPayReferenceKey(mobile);

                if (instantPayResult?.refKey) {
                    console.log('[AirtelImwDMT] InstantPay referenceKey =', instantPayResult.refKey);

                    setRegToken(instantPayResult.refKey);
                    setCustomerInfo(null);
                    setCustomerNotExist(true);
                    // Note: if you want to handle OTP here, check
                    // instantPayResult.needOtp and open an OTP modal.
                } else {
                    // Couldn't get a referenceKey → stay on search
                    setCustomerNotExist(false);
                    setCustomerInfo(null);
                }
            }

            // ---- Case 3: Ineligible ----
            else if (response.message?.includes("Customer Have Other Account with bank")) {
                setIneligibleReason(response.message);
                setCustomerNotExist(false);
                setCustomerInfo(null);
                toast.error(response.message);
            }

            // ---- Case 4: Anything else ----
            else {
                toast.error(response.message || "Failed to fetch customer info");
                setCustomerNotExist(true);
            }
        } catch (error) {
            console.error(error);
            toast.error("Error fetching customer info");
            setCustomerNotExist(true);
        } finally {
            setLoading(false);
        }
    };

    const getSenderLimit = async (mobile) => {
        if (isProcessing) return;
        const payload = { senderMobile: mobile };
        try {
            const response = await postRequest(AIRTEL_IMW_GET_SENDER_LIMIT, payload, history);
            if (response.success === true) {
                const data = response.data || {};
                setCustomerInfo({
                    customerMobile: mobile,
                    senderName: data.senderName || '',
                    monthlyLimit: data.monthlyLimit || 0,
                    limitAvailable: data.limitAvailable || 0,
                    consumed: data.consumed || 0,
                    isRegistered: true,
                });
                setCustomerNotExist(false);
            } else {
                toast.error(response.message || "Failed to fetch limit");
            }
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    };

    if (isLoading) {
        return (
            <div className="content text-center" style={{ margin: "250px 0" }}>
                <PropagateLoader color={primaryRed} loading={isLoading} size={25} />
            </div>
        );
    }

    // ✅ Registered → ViewCustomer owns the full layout. Render it bare.
    if (isCustomerInfo && !isCustomerNotExist) {
        return (
            <ViewCustomer
                isCustomerInfo={isCustomerInfo}
                findCustomerInfo={getCustomerInfo}
            />
        );
    }

    // Other states use the shared wrapper
    return (
        <section style={DMT_PAGE_WRAPPER}>
            <div style={{ position: 'absolute', top: -80, right: -80, width: 350, height: 350, background: 'radial-gradient(circle, rgba(255,221,9,0.18) 0%, transparent 70%)', borderRadius: '50%' }} />
            <div style={{ position: 'absolute', bottom: -40, left: -40, width: 250, height: 250, background: 'radial-gradient(circle, rgba(201,38,44,0.08) 0%, transparent 70%)', borderRadius: '50%' }} />

            <div style={DMT_PAGE_CONTAINER}>
                {ineligibleReason && (
                    <div className="alert alert-danger">
                        <strong>This Customer is not eligible to do AIRTEL DMT</strong><br />
                        Reason: {ineligibleReason}
                    </div>
                )}

                {/* ✅ Not registered → AddCustomer with InstantPay's referenceKey */}
                {(isCustomerNotExist && !ineligibleReason) && (
                    <AddCustomer
                        mobile={searchText}
                        referenceKey={regToken}
                        findCustomerInfo={getCustomerInfo}
                    />
                )}

                {(!props.mobile) && (
                    <div style={{
                        background: '#fff',
                        borderRadius: '20px',
                        padding: '50px 30px',
                        textAlign: 'center',
                        border: `1px solid ${cardBorder}`,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                    }}>
                        <i className="fa fa-users fa-4x" style={{ color: '#D1D5DB', marginBottom: '16px' }}></i>
                        <h5 style={{ color: '#1A1A2E', fontWeight: 600 }}>No mobile number provided</h5>
                        <p style={{ color: '#6B7280', marginBottom: 0 }}>
                            Please select a customer from the main search page.
                        </p>
                    </div>
                )}
            </div>
        </section>
    );
};

export default AirtelImwDMT;